import React, { useState } from 'react';
import { X, Send, Loader2, CheckCircle, Plus, Trash2 } from 'lucide-react';
import api from '../../../services/api';

const MAX_QUESTIONS = 10;
const DISCOUNT_PER_QUESTION = 10;

const emptyQuestion = () => ({
    questionText: '',
    answer1: '',
    answer2: '',
    answer3: '',
    answer4: '',
});

const SuggestVuQuestionModal = ({ levelId, language, onClose }) => {
    const fr = language === 'fr';
    const [questions, setQuestions] = useState([emptyQuestion()]);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const validQuestions = questions.filter(q => q.questionText.trim() && q.answer1.trim());
    const canSubmit = validQuestions.length > 0 && !submitting;

    const updateField = (index, field, value) => {
        setQuestions(prev => prev.map((q, i) => i === index ? { ...q, [field]: value } : q));
    };

    const addQuestion = () => {
        if (questions.length >= MAX_QUESTIONS) return;
        setQuestions(prev => [...prev, emptyQuestion()]);
    };

    const removeQuestion = (index) => {
        setQuestions(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            const results = await Promise.allSettled(
                validQuestions.map(q => api.suggestVuExamenQuestion(
                    levelId,
                    q.questionText.trim(),
                    q.answer1.trim(),
                    q.answer2.trim(),
                    q.answer3.trim(),
                    q.answer4.trim()
                ))
            );
            const succeeded = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.length - succeeded;
            setResult({ succeeded, failed });
            if (succeeded === 0) {
                setError(fr ? "Erreur lors de l'envoi. Veuillez réessayer." : 'Error sending suggestions. Please try again.');
                setResult(null);
            }
        } catch {
            setError(fr ? "Erreur lors de l'envoi. Veuillez réessayer." : 'Error sending suggestions. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const maxDiscount = MAX_QUESTIONS * DISCOUNT_PER_QUESTION;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <img src="/img/application/vu_icone.svg" alt="" className="w-8 h-8" />
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            {fr ? 'Suggérer des questions' : 'Suggest questions'}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition">
                        <X className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                {result ? (
                    <div className="text-center py-8">
                        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                        <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {fr ? 'Merci pour votre contribution !' : 'Thank you for your contribution!'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            {fr
                                ? `${result.succeeded} suggestion${result.succeeded > 1 ? 's' : ''} envoyée${result.succeeded > 1 ? 's' : ''}. Notre équipe les examinera et vous recevrez un code promo de ${DISCOUNT_PER_QUESTION} % pour chaque question ajoutée au référentiel.`
                                : `${result.succeeded} suggestion${result.succeeded > 1 ? 's' : ''} sent. Our team will review them and you'll receive a ${DISCOUNT_PER_QUESTION}% promo code for each question added to our database.`}
                            {result.failed > 0 && (
                                <span className="block mt-2 text-red-500">
                                    {fr
                                        ? `${result.failed} suggestion${result.failed > 1 ? 's n\'ont' : ' n\'a'} pas pu être envoyée${result.failed > 1 ? 's' : ''}.`
                                        : `${result.failed} suggestion${result.failed > 1 ? 's' : ''} could not be sent.`}
                                </span>
                            )}
                        </p>
                        <button onClick={onClose} className="px-6 py-2.5 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 transition">
                            {fr ? 'Fermer' : 'Close'}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Description */}
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
                            {fr
                                ? `Proposez les questions que vous avez vues lors de votre examen chez Transports Canada. Plus vous donnez de détails, plus vous aurez de chances de contribuer au référentiel.`
                                : `Submit the questions you saw during your Transport Canada exam. The more details you provide, the better your chances of contributing to our database.`}
                        </p>

                        {/* Discount info */}
                        <div className="mb-5 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30">
                            <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
                                {fr
                                    ? <><strong>{DISCOUNT_PER_QUESTION} % de rabais</strong> par question acceptée par notre équipe — <strong>jusqu'à {maxDiscount} %</strong> si vos {MAX_QUESTIONS} suggestions sont toutes ajoutées au référentiel.</>
                                    : <><strong>{DISCOUNT_PER_QUESTION}% discount</strong> per question accepted by our team — <strong>up to {maxDiscount}%</strong> if all {MAX_QUESTIONS} suggestions are added to our database.</>}
                            </p>
                        </div>

                        {/* Questions list */}
                        <div className="space-y-4 mb-4">
                            {questions.map((q, index) => (
                                <div key={index} className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02]">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {fr ? `Question ${index + 1}` : `Question ${index + 1}`}
                                        </span>
                                        {questions.length > 1 && (
                                            <button
                                                onClick={() => removeQuestion(index)}
                                                className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg text-red-500 transition"
                                                title={fr ? 'Supprimer' : 'Remove'}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Question text */}
                                    <div className="mb-3">
                                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                                            {fr ? 'Question' : 'Question'} <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            value={q.questionText}
                                            onChange={e => updateField(index, 'questionText', e.target.value)}
                                            placeholder={fr ? "Décrivez la question telle que vous l'avez vue..." : 'Describe the question as you saw it...'}
                                            rows={3}
                                            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:border-red-400 dark:focus:border-red-500 transition resize-none"
                                        />
                                    </div>

                                    {/* Answers */}
                                    <div className="space-y-2">
                                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400">
                                            {fr ? 'Choix de réponses' : 'Answer choices'}
                                        </label>
                                        <input
                                            type="text"
                                            value={q.answer1}
                                            onChange={e => updateField(index, 'answer1', e.target.value)}
                                            placeholder={fr ? 'Réponse 1 (obligatoire) *' : 'Answer 1 (required) *'}
                                            className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:border-red-400 dark:focus:border-red-500 transition"
                                        />
                                        {[
                                            { num: 2, field: 'answer2' },
                                            { num: 3, field: 'answer3' },
                                            { num: 4, field: 'answer4' },
                                        ].map(({ num, field }) => (
                                            <input
                                                key={num}
                                                type="text"
                                                value={q[field]}
                                                onChange={e => updateField(index, field, e.target.value)}
                                                placeholder={fr ? `Réponse ${num} (optionnel)` : `Answer ${num} (optional)`}
                                                className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:border-gray-400 dark:focus:border-white/20 transition"
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Add question button */}
                        <button
                            onClick={addQuestion}
                            disabled={questions.length >= MAX_QUESTIONS}
                            className="w-full py-3 mb-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-white/15 text-sm font-bold text-gray-600 dark:text-gray-300 hover:border-red-400 hover:text-red-500 dark:hover:border-red-500 dark:hover:text-red-400 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            {fr ? 'Ajouter une question' : 'Add a question'}
                            <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">
                                ({questions.length} / {MAX_QUESTIONS})
                            </span>
                        </button>

                        {/* Error */}
                        {error && (
                            <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 text-sm text-red-600 dark:text-red-400">
                                {error}
                            </div>
                        )}

                        {/* Submit */}
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                            {validQuestions.length > 1
                                ? (fr ? `Envoyer ${validQuestions.length} suggestions` : `Send ${validQuestions.length} suggestions`)
                                : (fr ? 'Envoyer la suggestion' : 'Send suggestion')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default SuggestVuQuestionModal;
